import { AnimatePresence, Point } from 'framer-motion';
import React, { useState } from 'react';

import { MediaCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import { ArticlesSliderVariants } from './ArticlesSlider.animations';
import { swipePower, wrap } from './ArticlesSlider.utils';

import * as S from './ArticleSlider.styles';

type SliderItemsTypes = {
  media: MediaCMS;
  caption: string;
};

export interface ArticleSliderProps {
  data: {
    sliderItems: Array<SliderItemsTypes>;
  };
  className?: string;
}

interface handleDragEndProps {
  offset: Point;
  velocity: Point;
}

const defaultProps: Partial<ArticleSliderProps> = {};

const ArticleSlider: React.FunctionComponent<ArticleSliderProps> = ({
  data,
  className,
}) => {
  const { sliderItems } = data;
  const [[page, direction], setPage] = useState([0, 0]);
  const swipeConfidenceThreshold = 100;
  const imageIndex = wrap(0, sliderItems?.length, page);
  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  const handleDragEnd = (
    e: MouseEvent | TouchEvent | PointerEvent,
    { offset, velocity }: handleDragEndProps
  ) => {
    const swipe = swipePower(offset.x, velocity.x);
    if (swipe < -swipeConfidenceThreshold) {
      paginate(1);
    } else if (swipe > swipeConfidenceThreshold) {
      paginate(-1);
    }
  };

  return (
    <S.ArticleSliderWrapper className={className}>
      <S.InnerContent>
        <S.Controls>
          <S.Svg onClick={() => paginate(-1)} />
          <S.Svg onClick={() => paginate(1)} />
        </S.Controls>
        <AnimatePresence initial={false} custom={direction}>
          <div>
            <S.Figure
              // key={page}
              custom={direction}
              variants={ArticlesSliderVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <S.ImageWrapper
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={handleDragEnd}
              >
                {sliderItems.map(item => (
                  <S.Image
                    key={item?.media?.url}
                    src={item?.media?.url}
                    alt={item?.media?.alternativeText}
                    style={{ x: `${imageIndex * -100}%` }}
                  />
                ))}
              </S.ImageWrapper>
            </S.Figure>
          </div>
        </AnimatePresence>
      </S.InnerContent>
      <S.ControlsWrapper>
        <S.ImageCounter>
          <S.TextWrapper>
            {imageIndex + 1 < 10
              ? `0${imageIndex + 1 || 0}`
              : `${imageIndex + 1 || 0}`}
            {'/'}
            {sliderItems.length < 10
              ? `0${sliderItems.length}`
              : `${sliderItems.length + 1}`}
          </S.TextWrapper>
          <S.CaptionWrapper>{sliderItems[imageIndex].caption}</S.CaptionWrapper>
        </S.ImageCounter>
      </S.ControlsWrapper>
    </S.ArticleSliderWrapper>
  );
};

ArticleSlider.defaultProps = defaultProps;

export default ArticleSlider;
